import React, { useState, useEffect } from "react";
import axios from "axios";
import { API_BASE_URL } from "../utils/urls.js";
import { BiSearch } from "react-icons/bi";

const AuditLogViewer = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const fetchLogs = async (page = 1, search = "") => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${API_BASE_URL}/super-admin/audit-logs`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { page, search, pageSize: 8 },
        }
      );
      setLogs(response.data.data);
      setTotalPages(response.data.metadata.totalPages);
      setCurrentPage(response.data.metadata.currentPage);
    } catch (err) {
      setError("Failed to fetch audit logs.");
    } finally {
      setLoading(false);
    }
  };

  // --- Changed ---
  // Simplified useEffect logic, similar to SuperAdminTransactions
  useEffect(() => {
    const handler = setTimeout(() => {
      // We pass currentPage so it respects the current page *unless* search changes
      // (which is handled by the next useEffect)
      fetchLogs(currentPage, searchQuery);
    }, 300); // Debounce search
    return () => clearTimeout(handler);
  }, [currentPage, searchQuery]);

  // Reset to page 1 when search query changes
  useEffect(() => {
    if (currentPage !== 1) {
      setCurrentPage(1);
    }
    // We only want this to run when searchQuery changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);
  // --- End Change ---

  // --- Removed ---
  // `handlePageChange` function is no longer needed
  // `getPaginationRange` function is no longer needed
  // --- End Removed ---

  return (
    <div className="p-4 sm:p-6 lg:p-8">
      {/* Header */}
      <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-6">
        System Audit Logs
      </h1>

      {/* Search Bar */}
      <div className="relative mb-6 w-full max-w-lg">
        <BiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          placeholder="Search by user email, action, or resource..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full pl-10 pr-4 py-2 border rounded-lg shadow-sm focus:ring focus:ring-blue-200 focus:border-blue-400"
        />
      </div>

      {/* Loading & Error States */}
      {loading ? (
        <p className="text-center text-gray-500">Loading...</p>
      ) : error ? (
        <p className="text-red-500 text-center">{error}</p>
      ) : logs.length === 0 ? (
        <p className="text-gray-500 text-center">No logs found.</p>
      ) : (
        <>
          {/* Desktop Table */}
          <div className="hidden md:block bg-white shadow-sm rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left font-semibold text-gray-600 uppercase text-xs">
                      User
                    </th>
                    <th className="px-6 py-3 text-left font-semibold text-gray-600 uppercase text-xs">
                      Action
                    </th>
                    <th className="px-6 py-3 text-left font-semibold text-gray-600 uppercase text-xs">
                      Resource
                    </th>
                    <th className="px-6 py-3 text-left font-semibold text-gray-600 uppercase text-xs">
                      Timestamp
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 bg-white">
                  {logs.map((log) => (
                    <tr key={log.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-gray-900">
                        {log.user_name}{" "}
                        <span className="text-gray-500 text-sm">
                          ({log.user_email})
                        </span>
                      </td>
                      <td className="px-6 py-4 text-gray-700">
                        {log.action}
                      </td>
                      <td className="px-6 py-4 text-gray-700">
                        {log.resource_type}{" "}
                        <span className="text-gray-500 text-xs">
                          (ID: {log.resource_id})
                        </span>
                      </td>
                      <td className="px-6 py-4 text-gray-600">
                        {new Date(log.created_at).toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* 📱 Mobile Card Layout */}
          <div className="md:hidden space-y-4">
            {logs.map((log) => (
              <div
                key={log.id}
                className="bg-white shadow-sm rounded-lg p-4 border border-gray-100"
              >
                <div className="flex justify-between items-center mb-2">
                  <p className="text-sm font-semibold text-gray-800">
                    {log.action}
                  </p>
                  <p className="text-xs text-gray-500">
                    {new Date(log.created_at).toLocaleString()}
                  </p>
                </div>
                <p className="text-sm text-gray-700">
                  <span className="font-medium">User:</span> {log.user_name}{" "}
                  <span className="text-gray-500 text-xs">
                    ({log.user_email})
                  </span>
                </p>
                <p className="text-sm text-gray-700 mt-1">
                  <span className="font-medium">Resource:</span>{" "}
                  {log.resource_type}{" "}
                  <span className="text-gray-500 text-xs">
                    (ID: {log.resource_id})
                  </span>
                </p>
              </div>
            ))}
          </div>

          {/* --- Changed Pagination --- */}
          {/* Replaced with the simple Previous/Next style */}
          {totalPages > 1 && (
            <div className="flex justify-between items-center mt-6">
              <button
                onClick={() =>
                  setCurrentPage((prev) => Math.max(prev - 1, 1))
                }
                disabled={currentPage === 1}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-blue-700 hover:text-black disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <span className="text-sm text-gray-700">
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={() =>
                  setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                }
                disabled={currentPage === totalPages}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-blue-700 hover:text-black disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          )}
          {/* --- End Pagination Change --- */}
        </>
      )}
    </div>
  );
};

export default AuditLogViewer;